import type { FilePreviewType } from './UploadReceiptCard';
import type { FC, RefObject } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Skeleton, Stack, styled, Typography } from '@mui/material';
import { EnlargeImageGallery } from '../EnlargeImageGallery';
import { ImageNavigationButtons } from '../ImageNavigationButtons';
import type { UrlTypeNameMapping } from '@pflegenavi/web-components';
import {
  Box,
  DeleteDialog,
  Iconify,
  PdfFileIcon,
} from '@pflegenavi/web-components';
import { useTheme } from '@mui/material/styles';
import { truncateFileNameIfNeeded } from './truncateFileNameIfNeeded';

interface ImagePreviewContainerProps {
  currentImageUrl: string;
  onSelectPrevious: () => void;
  onSelectNext: () => void;
  onDelete: VoidFunction;
  currentImageIndex: number;
  file: FilePreviewType;
  inputRef: RefObject<HTMLInputElement | null>;
  imageUrls: string[];
  imageUrlToTypeMap: UrlTypeNameMapping;
  onAddFile: (e: any) => void;
  onChangeFile: () => void;
  canEdit: boolean;
}

export const ImagePreviewContainer: FC<ImagePreviewContainerProps> = ({
  currentImageIndex,
  currentImageUrl,
  onDelete,
  onSelectNext,
  onSelectPrevious,
  imageUrls,
  imageUrlToTypeMap,
  file,
  onChangeFile,
  canEdit,
}) => {
  const [imageHovered, setImageHovered] = useState(false);
  const [imageZoomed, setImageZoomed] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { t } = useTranslation();

  const handleOnDelete = () => {
    setDeleteDialogOpen(true);
  };

  const disabledTemp = false;
  const isLoading = imageUrlToTypeMap.get(currentImageUrl) === undefined;

  const fileWithType = imageUrlToTypeMap.get(currentImageUrl);

  const isPdf = fileWithType?.type === 'application/pdf';
  const isImage = fileWithType?.type.startsWith('image/') ?? false;
  const fileName = fileWithType?.name ?? file?.name ?? 'unknown';

  return (
    <Stack
      sx={{
        minWidth: 340,
        minHeight: 140,
      }}
    >
      <EnlargeImageGallery
        open={imageZoomed}
        handleClose={() => setImageZoomed(false)}
        initialSelectedIndex={currentImageIndex}
        imageUrls={imageUrls}
        imageUrlToTypeMap={imageUrlToTypeMap}
      />

      <DeleteDialog
        open={deleteDialogOpen}
        handleDelete={onDelete}
        setDeleteDialogOpen={setDeleteDialogOpen}
        confirmationMessage={t('confirmDeletionText')}
      />
      {isLoading && (
        <Skeleton variant="rectangular" width="100%" height={324} />
      )}
      {!isLoading && (
        <picture
          onMouseEnter={() => !disabledTemp && setImageHovered(true)}
          onMouseLeave={() => setImageHovered(false)}
          style={{
            position: 'relative',
          }}
        >
          <ImageActionsOverlay imageHovered={imageHovered}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              p={1}
            >
              <Stack alignItems="flex-start" sx={{ flex: 1 }}>
                {canEdit && (
                  <ImageActionButton onClick={handleOnDelete}>
                    <Iconify icon="eva:trash-2-outline" fontSize={20} />
                  </ImageActionButton>
                )}
              </Stack>
              <ImageNavigationButtons
                currentImageIndex={currentImageIndex}
                imagesCount={imageUrls.length}
                showButtons={imageUrls.length > 1}
                onPrevious={onSelectPrevious}
                onNext={onSelectNext}
                disabledLeft={imageUrls.indexOf(currentImageUrl) === 0}
                disabledRight={
                  imageUrls.indexOf(currentImageUrl) === imageUrls.length - 1
                }
              />
              <Stack direction="row" justifyContent="flex-end" sx={{ flex: 1 }}>
                {canEdit && (
                  <ImageActionButton onClick={onChangeFile} sx={{ mr: 0.4 }}>
                    <Iconify icon="eva:edit-fill" fontSize={20} />
                  </ImageActionButton>
                )}
                <ImageActionButton
                  onClick={() => {
                    setImageZoomed(true);
                  }}
                >
                  <Iconify icon="akar-icons:enlarge" fontSize={20} />
                </ImageActionButton>
              </Stack>
            </Stack>
          </ImageActionsOverlay>

          <source
            src={
              typeof file === 'string'
                ? file
                : file
                ? file.preview ?? undefined
                : ''
            }
            style={{ zIndex: -1, borderRadius: 4 }}
          />
          {!isPdf && !isImage ? (
            <FileTypeNotSupportedCard
              text={t('common.file-type-not-supported', {
                fileType: fileWithType?.type,
              })}
            />
          ) : isPdf ? (
            <PdfDownloadCard fileUrl={currentImageUrl} fileName={fileName} />
          ) : (
            <img
              alt=""
              src={currentImageUrl}
              style={{ zIndex: -1, borderRadius: 4 }}
            />
          )}
        </picture>
      )}
    </Stack>
  );
};

interface ImageActionsOverlayProps {
  imageHovered: boolean;
}

const ImageActionButton = styled(IconButton)(({ theme }) =>
  theme.unstable_sx({
    borderRadius: 6,
    color: 'black',
    bgcolor: 'rgb(255,255,255, 0.5)',
    '&:hover': {
      bgcolor: 'rgb(255,255,255, 0.7)',
    },
  })
);

interface FileTypeNotSupportedCardProps {
  text: string;
}

const FileTypeNotSupportedCard: FC<FileTypeNotSupportedCardProps> = ({
  text,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: '100%',
        height: 213,
        backgroundColor: theme.palette.grey['200'],
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
      }}
    >
      <Typography
        width="50%"
        textAlign={'center'}
        sx={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          position: 'absolute',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {text}
      </Typography>
    </Box>
  );
};

interface PdfDownloadCardProps {
  fileUrl: string;
  fileName: string;
}

const PdfDownloadCard: FC<PdfDownloadCardProps> = ({ fileUrl, fileName }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const cleanedFileName = truncateFileNameIfNeeded(fileName);

  return (
    <Box
      sx={{
        height: 324,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
      }}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          backgroundColor: theme.palette.grey['200'],
          borderRadius: 2,
          objectFit: 'cover',
          width: '100%',
          height: '100%',
          gap: 3,
        }}
      >
        <a href={fileUrl} target="_blank" rel="noreferrer">
          <img
            alt="Click to download PDF file"
            src={PdfFileIcon}
            style={{
              height: 71,
              position: 'absolute',
              zIndex: 1,
              top: '34%',
              left: '42%',
            }}
          />
        </a>
        <Stack justifyContent="end" alignItems="center">
          <Typography
            paddingTop={14}
            color={theme.palette.grey['500']}
            variant="subtitle2"
          >
            {t('actions.click-icon-pdf')}
          </Typography>
          <Typography
            paddingTop={1}
            color={theme.palette.grey['500']}
            variant="subtitle2"
          >
            {t('file.file-name') + ': ' + cleanedFileName}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

const ImageActionsOverlay = styled(Box)<ImageActionsOverlayProps>(
  ({ imageHovered }) =>
    ({ theme }) =>
      theme.unstable_sx({
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1,
        backgroundColor: 'transparent',
        display: imageHovered ? 'flex' : 'none',
        flexDirection: 'column',
        alignItems: 'stretch',
      })
);
